import type { MeResponse, NavigationGroupDto } from '@an-crm/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import { House, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useId } from 'react';
import { BrandMark, Logo } from '@/components/brand/logo';
import { SoonBadge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { NavIcon } from '@/components/ui/nav-icon';
import { findNavMatch } from './navigation';

interface SidebarProps {
  nav: NavigationGroupDto[];
  me: MeResponse;
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  className?: string;
}

const labelMotion = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.18, delay: 0.08 } },
  exit: { opacity: 0, x: -6, transition: { duration: 0.1 } },
};

const navItemMotion = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const } },
  exit: { opacity: 0, x: -10, transition: { duration: 0.16 } },
};

function NavLink({
  to,
  label,
  icon,
  active,
  planned,
  collapsed,
  indicatorId,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: string;
  active: boolean;
  planned: boolean;
  collapsed: boolean;
  indicatorId: string;
  onNavigate?: (() => void) | undefined;
}) {
  return (
    <Tooltip content={planned ? `${label} · Coming soon` : label} side="right" disabled={!collapsed}>
      <Link
        to={to}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        aria-label={collapsed ? label : undefined}
        className={cn(
          'focus-ring group relative flex h-9 items-center gap-3 rounded-md px-2.5 text-sm font-medium transition-colors duration-150',
          active ? 'text-primary-text' : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
          collapsed && 'justify-center px-0',
        )}
      >
        {active && (
          <motion.span
            layoutId={indicatorId}
            className="absolute inset-0 rounded-md bg-primary-soft ring-1 ring-brand/15"
            transition={{ type: 'spring', stiffness: 520, damping: 42 }}
          >
            <span className="absolute top-1.5 bottom-1.5 -left-3 w-[3px] rounded-r-full bg-brand" />
          </motion.span>
        )}
        <motion.span layout="position" className="relative flex shrink-0">
          {icon === '__home' ? (
            <House
              className={cn(
                'size-[18px]',
                active ? 'text-brand' : 'text-fg-subtle group-hover:text-fg-muted',
              )}
              aria-hidden
            />
          ) : (
            <NavIcon
              name={icon}
              className={cn(
                'size-[18px]',
                active ? 'text-brand' : 'text-fg-subtle group-hover:text-fg-muted',
              )}
              aria-hidden
            />
          )}
          {planned && collapsed && (
            <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-accent" aria-hidden />
          )}
        </motion.span>
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.span
              key="label"
              {...labelMotion}
              className="relative flex min-w-0 flex-1 items-center justify-between gap-2"
            >
              <span className="truncate">{label}</span>
              {planned && <SoonBadge />}
            </motion.span>
          )}
        </AnimatePresence>
      </Link>
    </Tooltip>
  );
}

export function Sidebar({ nav, me, collapsed, onToggle, onNavigate, className }: SidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const match = findNavMatch(nav, pathname);
  const indicatorId = useId();

  return (
    <motion.aside
      layout
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      aria-label="Primary"
      className={cn(
        'flex h-full flex-col border-r border-border bg-surface',
        collapsed ? 'w-[72px]' : 'w-[264px]',
        className,
      )}
    >
      <motion.div
        layout="position"
        className={cn('flex h-16 shrink-0 items-center', collapsed ? 'justify-center px-2' : 'px-5')}
      >
        <Link to="/" onClick={onNavigate} className="focus-ring flex items-center rounded-md">
          {collapsed ? <BrandMark className="size-8" /> : <Logo className="text-[15px]" />}
          <span className="sr-only"> home</span>
        </Link>
      </motion.div>

      <LayoutGroup id="sidebar">
        <nav className="flex-1 overflow-x-hidden overflow-y-auto px-3 pt-2 pb-4">
          <ul className="flex flex-col gap-0.5">
            <li>
              <NavLink
                to="/"
                label="Home"
                icon="__home"
                active={pathname === '/'}
                planned={false}
                collapsed={collapsed}
                indicatorId={indicatorId}
                onNavigate={onNavigate}
              />
            </li>
          </ul>
          <AnimatePresence initial={false}>
            {nav.map((group) => (
              <motion.div
                key={group.group}
                layout="position"
                {...navItemMotion}
                className="mt-5"
                role="group"
                aria-labelledby={`nav-group-${group.group}`}
              >
                <div className="flex h-6 items-center px-2.5">
                  <AnimatePresence initial={false} mode="wait">
                    {collapsed ? (
                      <motion.span
                        key="rule"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="mx-auto h-px w-6 bg-border"
                      />
                    ) : (
                      <motion.span
                        key="label"
                        id={`nav-group-${group.group}`}
                        {...labelMotion}
                        className="text-[11px] font-semibold tracking-[0.08em] text-fg-subtle uppercase"
                      >
                        {group.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {collapsed && (
                    <span id={`nav-group-${group.group}`} className="sr-only">
                      {group.label}
                    </span>
                  )}
                </div>
                <ul className="mt-1 flex flex-col gap-0.5">
                  {/* entitlement changes arrive live (realtime): items slide in and out */}
                  <AnimatePresence initial={false}>
                    {group.items.map((item) => (
                      <motion.li key={item.key} layout="position" {...navItemMotion}>
                        <NavLink
                          to={item.path}
                          label={item.label}
                          icon={item.icon}
                          active={match?.item.key === item.key}
                          planned={item.status === 'planned'}
                          collapsed={collapsed}
                          indicatorId={indicatorId}
                          onNavigate={onNavigate}
                        />
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
              </motion.div>
            ))}
          </AnimatePresence>
        </nav>
      </LayoutGroup>

      <div
        className={cn(
          'flex shrink-0 items-center border-t border-border p-3',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div key="ctx" {...labelMotion} className="min-w-0 px-1.5">
              <p className="truncate text-xs font-medium text-fg">{me.tenant?.name ?? 'Platform console'}</p>
              <p className="truncate text-[11px] text-fg-subtle">
                {me.tenant ? `${me.enabledModules.length} modules enabled` : 'Super Admin'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        {onToggle && (
          <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
            <button
              type="button"
              onClick={onToggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              className="focus-ring flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-2 hover:text-fg"
            >
              {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
            </button>
          </Tooltip>
        )}
      </div>
    </motion.aside>
  );
}
